import {
  faChevronDown,
  faChevronRight
} from '@fortawesome/pro-regular-svg-icons';
import { faTriangleExclamation } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  Stack,
  Typography
} from '@mui/material';
import type { ReactNode } from 'react';
import { useMemo, useState } from 'react';

import {
  PREREQUISITE_MISSING_LABEL,
  PREREQUISITE_MISSING_MESSAGE
} from './MsDefenderIntegration.constants';
import { consentTileStyles } from './MsDefenderIntegration.styles';
import {
  AppCategory,
  AppType,
  ConsentStatus
} from './MsDefenderIntegration.types';
import {
  getCombinedTenantStatus,
  getResponseActionRowStatus,
  getSkippedResponseActionsCount,
  getStatusChipColor,
  getUiStatus
} from './MsDefenderIntegration.utils';
import { ResponseActionHealth } from './ResponseActionHealth';
import { ResponseActionHistoryDialog } from './ResponseActionHistoryDialog';
import type { TenantConnectionRowProps } from './TenantConnectionRow.types';

const TenantConnectionRow = ({
  tile,
  integration,
  categoryStateByTenant,
  hasAdminUpdate,
  isServerStateAvailable,
  hasDefenderIngestion,
  consentEligibility,
  pendingConsent,
  consentErrorTarget,
  onConsent,
  onDisconnect,
  isDisconnecting,
  hasDisconnectError
}: TenantConnectionRowProps) => {
  const tokenStatus = getUiStatus(integration);
  const isTokenError = tokenStatus === ConsentStatus.ERROR;
  const actionStatus = integration.integration_data.api_action_status;
  const isPermissionsRequired =
    tokenStatus === ConsentStatus.CONNECTED &&
    actionStatus === ConsentStatus.INSUFFICIENT_PERMISSIONS;
  const isLastActionFailed =
    tokenStatus === ConsentStatus.CONNECTED &&
    actionStatus === ConsentStatus.ERROR;
  const rawActionError =
    integration.integration_data.last_api_action_status_check_error;
  const combinedStatus = getCombinedTenantStatus(tokenStatus, actionStatus);
  const tenantId = integration.tenant_id ?? 'Tenant ID unavailable';
  const tenantState = integration.tenant_id
    ? categoryStateByTenant[integration.tenant_id]
    : undefined;
  const hasConflict = tenantState?.hasConflict ?? false;
  const isDefenderResponse =
    tile.appType === AppType.ENTRA_AND_DEFENDER_RESPONSE_ACTIONS;
  const isLegacyInvalid = isDefenderResponse && !hasDefenderIngestion;
  const displayStatus = isLegacyInvalid
    ? { status: ConsentStatus.ERROR, label: PREREQUISITE_MISSING_LABEL }
    : combinedStatus;
  const isConsentPending =
    pendingConsent?.existingIntegrationId === integration.id;
  const hasConsentError =
    consentErrorTarget?.existingIntegrationId === integration.id;
  const mutationDisabled =
    !hasAdminUpdate || !isServerStateAvailable || isConsentPending;
  const isResponseTile = tile.category === AppCategory.RESPONSE;
  const [isExpanded, setIsExpanded] = useState(false);
  const [isHistoryDialogOpen, setIsHistoryDialogOpen] = useState(false);

  const responseActions = useMemo(
    () =>
      (integration.integration_data.response_actions ?? []).filter(
        (action) =>
          action.tenant_id != null && action.tenant_id === integration.tenant_id
      ),
    [integration.integration_data.response_actions, integration.tenant_id]
  );

  const rowStatus = useMemo(
    () =>
      isResponseTile
        ? getResponseActionRowStatus(
            tokenStatus,
            isLegacyInvalid,
            responseActions
          )
        : displayStatus,
    [
      isResponseTile,
      displayStatus,
      isLegacyInvalid,
      tokenStatus,
      responseActions
    ]
  );

  const skippedCount = useMemo(
    () =>
      isResponseTile ? getSkippedResponseActionsCount(responseActions) : 0,
    [isResponseTile, responseActions]
  );

  const warningItems: { key: string; content: ReactNode }[] = [];
  if (hasConflict) {
    warningItems.push({
      key: 'conflict',
      content:
        'This tenant has both apps in this category. Disconnect one explicit connection before starting or changing consent.'
    });
  }
  if (isLegacyInvalid) {
    warningItems.push({
      key: 'prerequisite-missing',
      content: (
        <>
          <strong>Prerequisite missing.</strong> {PREREQUISITE_MISSING_MESSAGE}
        </>
      )
    });
  }
  if (isPermissionsRequired) {
    warningItems.push({
      key: 'permissions-required',
      content: (
        <>
          <strong>Permissions required.</strong> Microsoft blocked one or more
          response actions for this tenant because the app doesn&apos;t have the
          required Graph permissions. Reauthorize this tenant to grant them.
          {rawActionError && (
            <Typography variant="body2" sx={consentTileStyles.alertMessage}>
              {rawActionError}
            </Typography>
          )}
        </>
      )
    });
  }
  if (isLastActionFailed && !isResponseTile) {
    warningItems.push({
      key: 'last-action-failed',
      content: (
        <>
          <strong>Last action failed.</strong> Pondurance could not complete the
          most recent response action for this tenant, and no action was taken.
          Retry the action, or share the details with the Account Management
          team if this continues.
          {rawActionError && (
            <Typography variant="body2" sx={consentTileStyles.alertMessage}>
              {rawActionError}
            </Typography>
          )}
        </>
      )
    });
  }

  return (
    <Box sx={consentTileStyles.connectionRow}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        sx={consentTileStyles.connectionHeader}
      >
        <Stack
          direction="row"
          sx={{ gap: 0.75, alignItems: 'center', flexGrow: 1 }}
        >
          {isResponseTile && (
            <IconButton
              size="small"
              onClick={() => setIsExpanded((prev) => !prev)}
              aria-label={isExpanded ? 'Collapse row' : 'Expand row'}
              sx={{ p: 0.5, color: 'text.secondary' }}
            >
              <FontAwesomeIcon
                icon={isExpanded ? faChevronDown : faChevronRight}
                style={{ fontSize: '0.85rem' }}
              />
            </IconButton>
          )}
          <Typography
            sx={{ fontWeight: 600 }}
            variant="caption"
            component={'div'}
          >
            Tenant ID:{' '}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            {tenantId}
          </Typography>
          <Chip
            size="small"
            label={rowStatus.label}
            color={getStatusChipColor(rowStatus.status)}
            onClick={() => setIsExpanded((prev) => !prev)}
            sx={{ ml: 1, cursor: 'pointer' }}
          />
          {skippedCount > 0 && (
            <Chip
              size="small"
              color="warning"
              icon={
                <FontAwesomeIcon
                  icon={faTriangleExclamation}
                  style={{ fontSize: '0.8rem', marginLeft: '6px' }}
                />
              }
              label={skippedCount}
              onClick={() => setIsExpanded((prev) => !prev)}
              sx={{ ml: 0.75, cursor: 'pointer', fontWeight: 600 }}
            />
          )}
        </Stack>
        <Button
          variant="outlined"
          size="small"
          onClick={() =>
            onConsent({ targetTile: tile, existingIntegration: integration })
          }
          disabled={
            mutationDisabled ||
            hasConflict ||
            isLegacyInvalid ||
            !consentEligibility.isEligible
          }
        >
          {isConsentPending ? 'Opening...' : 'Reauthorize'}
        </Button>
        <Button
          variant="outlined"
          color="error"
          size="small"
          disabled={
            !hasAdminUpdate || !isServerStateAvailable || isDisconnecting
          }
          onClick={() => onDisconnect(integration)}
        >
          {isDisconnecting ? 'Disconnecting...' : 'Disconnect'}
        </Button>
      </Stack>

      {isExpanded && (
        <>
          {isResponseTile && (
            <ResponseActionHealth
              responseActions={responseActions}
              onViewHistory={() => setIsHistoryDialogOpen(true)}
            />
          )}

          {warningItems.length > 0 && (
            <Alert
              severity="warning"
              variant="outlined"
              sx={consentTileStyles.rowAlert}
            >
              <Box sx={consentTileStyles.warningStack}>
                {warningItems.map((item) => (
                  <Typography key={item.key} variant="body2" component="div">
                    {item.content}
                  </Typography>
                ))}
              </Box>
            </Alert>
          )}

          {isTokenError && (
            <Alert
              severity="error"
              variant="outlined"
              sx={consentTileStyles.rowAlert}
            >
              <Typography sx={{ fontWeight: 600 }} variant="body2">
                Reauthorization needed. The latest token check failed, so
                Pondurance can no longer confirm this connection is active.
              </Typography>
              {integration.integration_data
                .last_api_token_status_check_error && (
                <Typography variant="body2" sx={consentTileStyles.alertMessage}>
                  {
                    integration.integration_data
                      .last_api_token_status_check_error
                  }
                </Typography>
              )}
            </Alert>
          )}
        </>
      )}

      {hasDisconnectError && (
        <Alert
          severity="error"
          variant="outlined"
          sx={consentTileStyles.rowAlert}
        >
          Unable to disconnect tenant {tenantId}. Try again.
        </Alert>
      )}

      {hasConsentError && (
        <Alert
          severity="error"
          variant="outlined"
          sx={consentTileStyles.rowAlert}
        >
          Unable to start consent for this connection. The current connection
          remains unchanged. Try again or refresh for the latest status.
        </Alert>
      )}

      {isResponseTile && (
        <ResponseActionHistoryDialog
          open={isHistoryDialogOpen}
          onClose={() => setIsHistoryDialogOpen(false)}
          actions={responseActions}
          tenantId={tenantId}
        />
      )}
    </Box>
  );
};

export default TenantConnectionRow;
